import supabase from "./supabase"
import { createPublicNotification } from "./apiNotifications"
import { addToGoogleCalendar } from "./integrationService"
import { getAllItems, getItem, STORE_NAMES } from "../features/offline-mode/offlineDB"
import { create as dsCreate, update as dsUpdate, remove as dsRemove } from "./dataService"
import { resolveClinicUuid, resolveClinicIdentifiers } from "./clinicIds"
import { getAllowedClinicIdsForPermission, assertClinicAllowed } from "./clinicAccess"
import { resolveClinicIdBigint } from "./clinicIds"
import {
    normalizePlanLimits,
    requireActiveSubscription,
    assertMonthlyLimit,
} from "./subscriptionEnforcement"

export async function getAppointmentById(id) {
    let allowedClinicIds = []
    try {
        allowedClinicIds = await getAllowedClinicIdsForPermission("appointments")
    } catch {
        const clinicUuid = await resolveClinicUuid()
        if (clinicUuid) allowedClinicIds = [clinicUuid]
    }
    if (allowedClinicIds.length === 0) throw new Error("User has no clinic assigned")

    const { data, error } = await supabase
        .from("appointments")
        .select(`
            *,
            patient:patients(*)
        `)
        .eq("id", id)
        .in("clinic_id", allowedClinicIds)
        .single()

    if (error) {
        console.error("Error fetching appointment:", error)
        throw new Error("فشل في تحميل تفاصيل الموعد")
    }

    return data
}

export async function getAppointments(search, page, pageSize, filters = {}) {
    const offlineEnabled = (() => {
        try { return localStorage.getItem("tabibi_offline_enabled") === "true" } catch { return false }
    })()
    const browserOffline = typeof navigator !== "undefined" && navigator.onLine === false
    if (offlineEnabled && browserOffline) {
        const all = await getAllItems(STORE_NAMES.APPOINTMENTS)
        const selectedClinicId = filters?.clinicId && filters.clinicId !== "all" ? String(filters.clinicId) : null
        const scoped = selectedClinicId ? all.filter((a) => String(a?.clinic_id || "") === selectedClinicId) : all

        const attachPatient = async (a) => {
            const pid = a?.patient_id
            if (pid == null) return { ...a, patient: null }
            const p = await getItem(STORE_NAMES.PATIENTS, pid)
            return { ...a, patient: p ? { id: p.id, name: p.name, phone: p.phone } : null }
        }

        let items = await Promise.all(scoped.map(attachPatient))

        if (filters.status && filters.status !== "all") {
            items = items.filter((a) => String(a?.status || "") === String(filters.status))
        }
        if (filters.source && filters.source !== "all") {
            items = items.filter((a) => String(a?.from || "") === String(filters.source))
        }
        if (filters.patientId) {
            items = items.filter((a) => String(a?.patient_id || "") === String(filters.patientId))
        }

        if (filters.time === "upcoming") {
            const today = new Date().toLocaleDateString('en-CA')
            items = items.filter((a) => String(a?.date || "") >= today)
            items.sort((a, b) => String(a?.date || "").localeCompare(String(b?.date || "")))
        } else {
            items.sort((a, b) => String(b?.date || "").localeCompare(String(a?.date || "")))
        }

        const q = String(search || "").trim()
        if (q) {
            const isIdSearch = /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|\d+)$/.test(q)
            if (isIdSearch) {
                items = items.filter((a) => String(a?.id || "") === q)
            } else {
                const qq = q.toLowerCase()
                items = items.filter((a) => {
                    const name = String(a?.patient?.name || "").toLowerCase()
                    const phone = String(a?.patient?.phone || "").toLowerCase()
                    return name.includes(qq) || phone.includes(qq)
                })
            }
        }

        const from = Math.max(0, (page - 1) * pageSize)
        const to = from + pageSize
        const pageItems = items.slice(from, to).map((a) => ({
            id: a.id,
            date: a.date,
            notes: a.notes,
            price: a.price,
            status: a.status,
            from: a.from,
            age: a.age,
            patient: a.patient,
            created_at: a.created_at,
            created_by_user_id: a.created_by_user_id,
            appointment_type: a.appointment_type
        }))
        return { data: pageItems, count: items.length }
    }

    const selectedClinicId = filters?.clinicId && filters.clinicId !== "all" ? String(filters.clinicId) : null
    let allowedClinicIds = []
    try {
        allowedClinicIds = await getAllowedClinicIdsForPermission("appointments")
    } catch {
        const clinicUuid = await resolveClinicUuid()
        if (clinicUuid) allowedClinicIds = [clinicUuid]
    }
    if (allowedClinicIds.length === 0) throw new Error("User has no clinic assigned")
    if (selectedClinicId) {
        if (!allowedClinicIds.includes(selectedClinicId)) throw new Error("غير مسموح لك الوصول لهذا الفرع")
        allowedClinicIds = [selectedClinicId]
    }

    console.log("getAppointments/start", {
        clinicIds: allowedClinicIds,
        search,
        page,
        pageSize,
        filters
    })

    const from = Math.max(0, (page - 1) * pageSize)
    const to = from + pageSize - 1

    // Detect if search is appointment ID (UUID or strict ID)
    const isIdSearch = !!search && /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|\d+)$/.test(search.trim())

    // Determine if we need inner join for patient search only
    const patientRelation = (!search || isIdSearch) 
        ? 'patient:patients(id, name, phone)' 
        : 'patient:patients!inner(id, name, phone)'
    console.log("getAppointments/patientRelation", patientRelation)

    let query = supabase
        .from("appointments")
        .select(`
      id,
      date,
      notes,
      price,
      status,
      from,
      age,
      clinic_id,
      created_by_user_id,
      appointment_type,
      ${patientRelation},
      created_at
    `, { count: "exact" })
        .in("clinic_id", allowedClinicIds)

    // Apply time filter
    if (filters.time === "upcoming") {
        // Use YYYY-MM-DD format to ensure we include today's appointments
        // Comparing date column (if it's just date) with ISO string (with time) would exclude today
        const today = new Date().toLocaleDateString('en-CA')
        query = query.gte('date', today)

        query = query.order("status", { ascending: false })
            .order("date", { ascending: true })
        console.log("getAppointments/timeFilter", { type: "upcoming", today })
    } else {
        // For all appointments, sort by date descending (newest first)
        // But for online bookings (source: booking), prioritize pending status
        if (filters.source === "booking") {
            // Custom sorting: pending status first, then by created_at (newest first)
            // We'll handle this sorting manually after fetching the data
            query = query.order("created_at", { ascending: false })
            console.log("getAppointments/sourceFilter", { source: "booking" })
        } else {
            query = query.order("date", { ascending: false })
            console.log("getAppointments/sourceFilter", { source: filters.source || "all" })
        }
    }

    // Apply range for pagination
    query = query.range(from, to)
    console.log("getAppointments/pagination", { from, to })

    // Apply date range filter if provided
    if (filters.dateFrom || filters.dateTo) {
        if (filters.dateFrom) {
            const startDate = new Date(filters.dateFrom)
            startDate.setHours(0, 0, 0, 0)
            query = query.gte('date', startDate.toISOString())
            console.log("getAppointments/dateFrom", startDate.toISOString())
        }
        if (filters.dateTo) {
            const endDate = new Date(filters.dateTo)
            endDate.setHours(23, 59, 59, 999)
            query = query.lte('date', endDate.toISOString())
            console.log("getAppointments/dateTo", endDate.toISOString())
        }
    } else if (filters.date) {
        // Single date filter (for backwards compatibility)
        const startDate = new Date(filters.date)
        startDate.setHours(0, 0, 0, 0)
        const endDate = new Date(startDate)
        endDate.setHours(23, 59, 59, 999)

        query = query
            .gte('date', startDate.toISOString())
            .lte('date', endDate.toISOString())
        console.log("getAppointments/date", { start: startDate.toISOString(), end: endDate.toISOString() })
    }

    if (filters.patientId) {
        query = query.eq('patient_id', filters.patientId)
    }

    // Apply status filter if provided (skip if "all")
    if (filters.status && filters.status !== "all") {
        query = query.eq('status', filters.status)
        console.log("getAppointments/statusFilter", filters.status)
    }

    // Apply source filter if provided (skip if "all")
    if (filters.source && filters.source !== "all") {
        query = query.eq('from', filters.source)
        console.log("getAppointments/sourceEqFilter", filters.source)
    }
    
    // Apply notes filter if provided
    if (filters.hasNotes) {
        query = query.not('notes', 'is', null)
        query = query.neq('notes', '')
    }

    // Apply search by ID or by patient fields
    if (search) {
        if (isIdSearch) {
            // Exact match on appointment ID within the same clinic
            query = query.eq('id', search.trim())
            console.log("getAppointments/searchById", search)
        } else {
            // Patient name/phone search
            query = query.or(`name.ilike.%${search}%,phone.ilike.%${search}%`, { foreignTable: 'patients' })
            console.log("getAppointments/search", search)
        }
    }

    const { data, error, count } = await query
    console.log("getAppointments/result", { count, length: (data || []).length })

    if (error) {
        console.error("Error fetching appointments:", error)
        console.log("getAppointments/errorDetails", { code: error.code, message: error.message })
        throw new Error("فشل في تحميل المواعيد")
    }

    // If source=booking, manually sort pending items to top
    let sortedData = data || []
    if (filters.source === "booking") {
        sortedData = sortedData.sort((a, b) => {
            if (a.status === "pending" && b.status !== "pending") return -1
            if (a.status !== "pending" && b.status === "pending") return 1
            return 0
        })
        console.log("getAppointments/sortedForBooking", { length: sortedData.length })
    }

    return {
        data: sortedData,
        count
    }
}

export async function createAppointment(payload) {
    const offlineEnabled = (() => {
        try { return localStorage.getItem("tabibi_offline_enabled") === "true" } catch { return false }
    })()
    const browserOffline = typeof navigator !== "undefined" && navigator.onLine === false
    if (offlineEnabled && browserOffline) {
        const clinicUuid = payload?.clinic_id || (() => {
            try { return localStorage.getItem("tabibi_clinic_id") } catch { return null }
        })()
        const appointmentData = {
            ...payload,
            clinic_id: clinicUuid,
            status: payload?.status || "confirmed",
            from: payload?.from || "clinic",
            updated_at: new Date().toISOString()
        }
        return dsCreate("appointments", appointmentData)
    }

    const selectedClinicUuid = payload?.clinic_id || await resolveClinicUuid()
    if (!selectedClinicUuid) throw new Error("User has no clinic assigned")
    await assertClinicAllowed("appointments", selectedClinicUuid)

    // Gating on active subscription. If not active (temporary/debug), fall back to allow creation
    let subscription = null;
    try {
      subscription = await requireActiveSubscription(selectedClinicUuid);
    } catch (err) {
      console.warn("No active subscription detected or error checking subscription. Proceeding with fallback.", err?.message || err);
    }
    const limits = subscription?.plans?.limits ? normalizePlanLimits(subscription.plans.limits) : {};

    if (!payload?.date) {
        throw new Error("تاريخ الموعد مطلوب")
    }

    await assertMonthlyLimit({
        clinicId: selectedClinicUuid,
        table: "appointments",
        dateColumn: "date",
        monthDate: payload.date,
        maxAllowed: limits.maxAppointments,
        errorMessage: "لقد تجاوزت الحد المسموح من المواعيد لهذا الشهر. يرجى ترقية الباقة.",
    })
    
    // Add clinic_id to the appointment data (UUID type for appointments table)
    const { data: { user } } = await supabase.auth.getUser()
    const appointmentData = {
        ...payload,
        clinic_id: selectedClinicUuid,
        status: payload.status || "confirmed", // Use provided status or default to "confirmed" (clinic bookings are pre-verified)
        from: payload.from || "clinic", // Use provided source or default to "clinic"
        created_by_user_id: user?.id || null
    }

    const { data, error } = await supabase
        .from("appointments")
        .insert(appointmentData)
        .select(`
            *,
            patient:patients(name)
        `)
        .single()

    if (error) throw error
    
    // --- Google Calendar Integration Hook ---
    try {
        if (data) {
            const patientName = data.patient?.name || "مريض";
            // Use deterministic ID to prevent duplicates if bulk sync runs later
            const googleEventId = `tabibiapp${data.id}`;
            await addToGoogleCalendar({
                ...data,
                patient_name: patientName
            }, null, googleEventId);
        }
    } catch (integrationError) {
        console.error("Calendar sync failed:", integrationError);
        // Don't fail the main request, just log it
    }
    // ----------------------------------------

    // --- WhatsApp Integration Hook Removed ---
    // ---------------------------------

    return data
}

export async function updateAppointment(id, payload) {
    const offlineEnabled = (() => {
        try { return localStorage.getItem("tabibi_offline_enabled") === "true" } catch { return false }
    })()
    const browserOffline = typeof navigator !== "undefined" && navigator.onLine === false
    if (offlineEnabled && browserOffline) {
        return dsUpdate("appointments", id, { ...payload, updated_at: new Date().toISOString() })
    }

    let allowedClinicIds = []
    try {
        allowedClinicIds = await getAllowedClinicIdsForPermission("appointments")
    } catch {
        const clinicUuid = await resolveClinicUuid()
        if (clinicUuid) allowedClinicIds = [clinicUuid]
    }
    if (allowedClinicIds.length === 0) throw new Error("User has no clinic assigned")

    console.log("updateAppointment payload:", payload, "id:", id);

    const { data, error } = await supabase
        .from("appointments")
        .update(payload)
        .eq("id", id.toString())  // Convert to string for compatibility
        .in("clinic_id", allowedClinicIds)
        .select()
        .single()

    if (error) {
        console.error("Error updating appointment:", error);
        console.error("Attempted to update appointment ID:", id, "at clinicIds:", allowedClinicIds);
        throw error;
    }

    // Auto-create financial record when appointment is marked as completed with price
    if (data && payload.status === "completed" && data.price && data.price > 0) {
        try {
            const clinicIdBigint = await resolveClinicIdBigint()
            if (clinicIdBigint) {
                // Check if financial record already exists for this appointment
                const { data: existingRecord } = await supabase
                    .from("financial_records")
                    .select("id")
                    .eq("appointment_id", data.id)
                    .single()
                
                if (!existingRecord) {
                    // Create income record for the appointment payment
                    const { error: financialError } = await supabase
                        .from("financial_records")
                        .insert({
                            clinic_id: clinicIdBigint,
                            appointment_id: data.id,
                            patient_id: data.patient_id,
                            amount: data.price,
                            type: "income",
                            description: `دفع مقابل موعد - ${data.patient?.name || 'مريض'}`,
                            recorded_at: new Date().toISOString()
                        })
                    
                    if (financialError) {
                        console.error("Error creating financial record:", financialError)
                    } else {
                        console.log("Financial record created for completed appointment:", data.id)
                    }
                }
            }
        } catch (err) {
            console.error("Error in auto financial record creation:", err)
        }
    }

    return data
}

export async function deleteAppointment(id) {
    const offlineEnabled = (() => {
        try { return localStorage.getItem("tabibi_offline_enabled") === "true" } catch { return false }
    })()
    const browserOffline = typeof navigator !== "undefined" && navigator.onLine === false
    if (offlineEnabled && browserOffline) {
        await dsRemove("appointments", id)
        return
    }

    let allowedClinicIds = []
    try {
        allowedClinicIds = await getAllowedClinicIdsForPermission("appointments")
    } catch {
        const clinicUuid = await resolveClinicUuid()
        if (clinicUuid) allowedClinicIds = [clinicUuid]
    }
    if (allowedClinicIds.length === 0) throw new Error("User has no clinic assigned")

    const { clinicIdBigint } = await resolveClinicIdentifiers()
    const { data: apptRow } = await supabase
        .from("appointments")
        .select("clinic_id")
        .eq("id", id.toString())
        .in("clinic_id", allowedClinicIds)
        .maybeSingle()

    const appointmentClinicId = apptRow?.clinic_id

    // Delete related records first to avoid foreign key constraints
    // 1. Delete notifications
    await supabase
        .from("notifications")
        .delete()
        .eq("appointment_id", id.toString())
        .eq("clinic_id", appointmentClinicId);

    // 2. Delete financial records (uses clinicIdBigint)
    if (clinicIdBigint) {
        await supabase
            .from("financial_records")
            .delete()
            .eq("appointment_id", id)
            .eq("clinic_id", clinicIdBigint);
    }

    // 3. Delete discount redemptions
    await supabase
        .from("discount_redemptions")
        .delete()
        .eq("appointment_id", id);

    // 4. Delete WhatsApp logs if the table exists
    try {
        await supabase
            .from("whatsapp_message_logs")
            .delete()
            .eq("appointment_id", id)
            .eq("clinic_id", appointmentClinicId);
    } catch (err) {
        // Table might not exist or other issues, ignore
        console.warn("Could not delete WhatsApp logs:", err.message);
    }

    // Finally delete the appointment itself
    const { error } = await supabase
        .from("appointments")
        .delete()
        .eq("id", id.toString())  // Convert to string for compatibility
        .in("clinic_id", allowedClinicIds)

    if (error) {
        console.error("Error deleting appointment:", error);
        console.error("Attempted to delete appointment ID:", id, "at clinicIds:", allowedClinicIds);
        throw error;
    }
}

export async function searchPatients(searchTerm, clinicId) {
    const clinicUuid = clinicId || await resolveClinicUuid()
    if (!clinicUuid) throw new Error("User has no clinic assigned")

    const { data: clinicRow, error: clinicErr } = await supabase
        .from("clinics")
        .select("owner_user_id")
        .eq("clinic_uuid", clinicUuid)
        .single()

    if (clinicErr) throw clinicErr
    const ownerUserId = clinicRow?.owner_user_id
    if (!ownerUserId) throw new Error("تعذر تحديد مالك الحساب")

    const { data, error } = await supabase
        .from("patients")
        .select("id, name, phone")
        .eq("owner_user_id", ownerUserId)
        .or(`name.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`)
        .limit(5)

    if (error) throw new Error("فشل البحث عن المرضى")

    return data
}

export async function createAppointmentPublic(payload, clinicId) {
    // Convert clinicId to string for JSON serialization
    const clinicIdString = clinicId.toString();

    const subscription = await requireActiveSubscription(clinicIdString)
    const limits = normalizePlanLimits(subscription?.plans?.limits)

    if (!payload?.date) {
        throw new Error("تاريخ الموعد مطلوب")
    }

    await assertMonthlyLimit({
        clinicId: clinicIdString,
        table: "appointments",
        dateColumn: "date",
        monthDate: payload.date,
        maxAllowed: limits.maxAppointments,
        errorMessage: "لقد تجاوزت الحد المسموح من المواعيد لهذا الشهر. يرجى ترقية الباقة.",
    })

    // Check for appointment conflicts if enabled
    const { data: clinicData } = await supabase
        .from('clinics')
        .select('prevent_conflicts, min_time_gap')
        .eq('clinic_uuid', clinicIdString)
        .single();

    if (clinicData?.prevent_conflicts) {
        const gapMinutes = parseInt(clinicData.min_time_gap) || 0;
        const appointmentDate = new Date(payload.date);
        
        // Calculate range to check for conflicts
        const startDate = new Date(appointmentDate.getTime() - (gapMinutes - 1) * 60000);
        const endDate = new Date(appointmentDate.getTime() + (gapMinutes - 1) * 60000);

        const { data: conflicts } = await supabase
            .from('appointments')
            .select('id')
            .eq('clinic_id', clinicIdString)
            .neq('status', 'cancelled')
            .gte('date', startDate.toISOString())
            .lte('date', endDate.toISOString())
            .limit(1);

        if (conflicts && conflicts.length > 0) {
            throw new Error(`عذراً، هذا الوقت غير متاح. يرجى اختيار وقت آخر (يوجد تضارب مع حجز آخر في غضون ${gapMinutes} دقيقة)`);
        }
    }

    // Check if phone is blocked (Shadow Ban)
    // If phone is provided in payload, check against blocked_phones table
    if (payload.phone) {
        try {
            const { data: blockedData } = await supabase
                .from('blocked_phones')
                .select('id')
                .eq('clinic_id', clinicIdString)
                .eq('phone_number', payload.phone)
                .maybeSingle();
                
            if (blockedData) {
                console.log("Blocked number attempted to book. Applying Shadow Ban:", payload.phone);
                // Return fake success response (Shadow Ban)
                // The user thinks it succeeded, but nothing is saved to appointments
                return {
                    id: "blocked-" + Date.now(),
                    status: "pending",
                    clinic_id: clinicIdString,
                    ...payload,
                    created_at: new Date().toISOString()
                };
            }
        } catch (err) {
            console.error("Error checking blocked status:", err);
            // Proceed if check fails to avoid blocking legitimate users on error
        }
    }

    // Remove phone from payload as it's not a column in appointments table
    // It was only passed for the blocking check
    const { phone, ...restPayload } = payload;

    // Add clinic_id to the appointment data
    const appointmentData = {
        ...restPayload,
        clinic_id: clinicIdString,
        status: "pending",
        from: "booking" // Indicate that this appointment was created from the booking page
    }

    const { data, error } = await supabase
        .from("appointments")
        .insert(appointmentData)
        .select()
        .single()

    if (error) {
        console.error("Error creating appointment:", error);
        throw error
    }
    
    // Debug logging to see what IDs we're getting
    console.log("Appointment created with data:", data);
    console.log("Payload patient:", payload.patient);
    
    // Validate that we have a proper ID for the appointment
    if (!data.id) {
        console.error("Appointment ID is missing from created appointment");
        return data;
    }
    
    // Create notification for the new appointment
    // COMMENTED OUT: The database trigger 'on_new_appointment' already creates a notification.
    // Keeping this enabled causes duplicate notifications (Double Trigger).
    /*
    try {
        await createPublicNotification({
            clinic_id: clinicIdString,
            title: "حجز جديد",
            message: `لديك حجز جديد من ${payload.patient?.name || 'مريض'} في ${new Date(payload.date).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}`,
            type: "appointment",
            // Pass the appointment ID - it should be a UUID
            appointment_id: data.id
        });
    } catch (notificationError) {
        console.error("Error creating notification:", notificationError);
        // Don't throw error here as we still want to return the appointment data
    }
    */

    // --- WhatsApp Integration Hook Removed ---
    // ---------------------------------

    return data
}

export async function getPatientAppointments(patientId) {
    let allowedClinicIds = []
    try {
        allowedClinicIds = await getAllowedClinicIdsForPermission("appointments")
    } catch {
        const clinicUuid = await resolveClinicUuid()
        if (clinicUuid) allowedClinicIds = [clinicUuid]
    }
    if (allowedClinicIds.length === 0) throw new Error("User has no clinic assigned")

    const { data, error } = await supabase
        .from("appointments")
        .select(`
            id,
            date,
            notes,
            price,
            status,
            from,
            created_at
        `)
        .in("clinic_id", allowedClinicIds)
        .eq("patient_id", patientId)
        .order("date", { ascending: false })

    if (error) {
        console.error("Error fetching patient appointments:", error)
        throw new Error("فشل في تحميل مواعيد المريض")
    }

    return data
}



export async function searchPatientsPublic(phone, clinicId) {
    if (!clinicId) throw new Error("Clinic ID is required");
    const { data: clinicRow, error: clinicErr } = await supabase
        .from("clinics")
        .select("owner_user_id")
        .eq("clinic_uuid", clinicId.toString())
        .single()

    if (clinicErr) throw clinicErr
    const ownerUserId = clinicRow?.owner_user_id
    if (!ownerUserId) throw new Error("تعذر تحديد مالك الحساب")
    
    const { data, error } = await supabase
        .from("patients")
        .select("id, name, phone, gender, age")
        .eq("owner_user_id", ownerUserId)
        .eq("phone", phone)
        .limit(1)

    if (error) {
        console.error("Error searching patients:", error)
        throw new Error("فشل البحث عن المرضى")
    }

    return data
}

export function subscribeToAppointments(callback, clinicId, sourceFilter = null) {
    if (!clinicId) return () => {}

    console.log("subscribeToAppointments/start", { clinicId, sourceFilter })

    const subscription = supabase
        .channel(`appointments_changes_${clinicId}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'appointments',
                filter: `clinic_id=eq.${clinicId}`
            },
            (payload) => {
                console.log("subscribeToAppointments/event", {
                    eventType: payload.eventType,
                    newRow: payload.new,
                    oldRow: payload.old
                })
                callback(payload)
            }
        )
        .subscribe()

    return () => {
        console.log("subscribeToAppointments/cleanup", { clinicId })
        supabase.removeChannel(subscription)
    }
}

export async function getUserByAuthUid(authUid) {
    if (!authUid) return null
    const { data, error } = await supabase
        .from("users")
        .select("id, name, role, auth_uid")
        .eq("auth_uid", authUid)
        .single()
    if (error) {
        console.warn("getUserByAuthUid/error", error.message)
        return null
    }
    return data
}

export async function getUsersByAuthUids(authUids) {
    if (!authUids || authUids.length === 0) return []
    // Search by user_id which matches the auth.users UUID
    const { data, error } = await supabase
        .from("users")
        .select("id, name, role, user_id, auth_uid")
        .in("user_id", authUids)
    if (error) {
        console.warn("getUsersByAuthUids/error", error.message)
        return []
    }
    console.log("getUsersByAuthUids/found:", data)
    return data || []
}
